import React from 'react';
import CapacityTimeline from './CapacityTimeline';
import './ServiceTileSummary.css';

const DAY_CHIPS = [
  { letter: 'M', index: 1 },
  { letter: 'T', index: 2 },
  { letter: 'W', index: 3 },
  { letter: 'Th', index: 4 },
  { letter: 'F', index: 5 },
  { letter: 'Sa', index: 6 },
  { letter: 'Su', index: 0 },
];

const formatTimeLabel = (raw) => {
  if (!raw || typeof raw !== 'string') return '—';
  const [h, m] = raw.split(':').map((part) => Number(part));
  if (Number.isNaN(h) || Number.isNaN(m)) return raw;
  const meridiem = h >= 12 ? 'PM' : 'AM';
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${meridiem}`;
};

const formatLunchLabel = (raw) => {
  if (!raw) return 'None';
  if (typeof raw === 'string') return formatTimeLabel(raw);
  if (raw.start && raw.end) {
    const startLabel = formatTimeLabel(raw.start).replace(/\s[AP]M$/, '');
    return `${startLabel}–${formatTimeLabel(raw.end)}`;
  }
  return 'None';
};

/**
 * F-final — Read-only summary view for a service tile.
 *
 * Renders the compact "design" body: 4-column summary (OPENS / CLOSES /
 * LUNCH BREAK / OPERATING DAYS), the TODAY · CAPACITY timeline, and a
 * hold-status footer. Used when the tile is NOT in edit mode; the
 * editable form fields are shown only after the pencil opens edit mode.
 */
const ServiceTileSummary = ({
  openTime,
  closeTime,
  lunchBreak,
  activeDays,
  timeline,
}) => {
  const days = Array.isArray(activeDays) ? activeDays : DAY_CHIPS.map((c) => c.index);

  return (
    <div className="appointments-v3-tile-summary">
      <div className="appointments-v3-tile-summary-grid">
        <div className="appointments-v3-tile-summary-col">
          <span className="appointments-v3-tile-summary-overline">OPENS</span>
          <span className="appointments-v3-tile-summary-value">{formatTimeLabel(openTime)}</span>
        </div>
        <div className="appointments-v3-tile-summary-col">
          <span className="appointments-v3-tile-summary-overline">CLOSES</span>
          <span className="appointments-v3-tile-summary-value">{formatTimeLabel(closeTime)}</span>
        </div>
        <div className="appointments-v3-tile-summary-col">
          <span className="appointments-v3-tile-summary-overline">LUNCH BREAK</span>
          <span className="appointments-v3-tile-summary-value">{formatLunchLabel(lunchBreak)}</span>
        </div>
        <div className="appointments-v3-tile-summary-col is-days">
          <span className="appointments-v3-tile-summary-overline">OPERATING DAYS</span>
          <div className="appointments-v3-tile-summary-days">
            {DAY_CHIPS.map((c) => (
              <span
                key={c.index}
                className={`appointments-v3-tile-summary-day ${days.includes(c.index) ? 'is-on' : ''}`}
                aria-label={`${c.letter}${days.includes(c.index) ? ' active' : ' inactive'}`}
              >
                {c.letter}
              </span>
            ))}
          </div>
        </div>
      </div>

      {timeline && (
        <div className="appointments-v3-tile-summary-timeline">
          <div className="appointments-v3-tile-summary-timeline-head">
            <span className="appointments-v3-tile-summary-overline">TODAY · CAPACITY</span>
            <span className="appointments-v3-tile-summary-counts">
              <span className="appointments-v3-tile-summary-count">{timeline.slotsTotal ?? 0} slots</span>
              <span className="appointments-v3-tile-summary-sep">·</span>
              <span className="appointments-v3-tile-summary-count is-booked">
                {timeline.booked ?? 0} booked
              </span>
              <span className="appointments-v3-tile-summary-sep">·</span>
              <span className="appointments-v3-tile-summary-count is-open">
                {timeline.open ?? 0} open
              </span>
            </span>
          </div>
          <CapacityTimeline
            openHour={timeline.openHour}
            closeHour={timeline.closeHour}
            slotMinutes={timeline.slotMinutes}
            bookedSlots={timeline.bookedSlots}
            lunchBreak={timeline.lunchRange}
          />
        </div>
      )}

    </div>
  );
};

export default ServiceTileSummary;
